import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { BeatLoader } from "react-spinners";
import { areaCamerasService } from "../../../services/areaCamerasService";
import CameraPhotoViewer from "../../../components/guardian/AreaCameras/PhotoViewer";

const LATEST = 24;
const PHOTO_WIDTH = 220;

const Strip = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Track = styled.div`
  display: flex;
  gap: 12px;
  width: max-content;
  transform: translate3d(${({ $x }) => $x}px, 0, 0);
`;

const Slide = styled.figure`
  margin: 0;
  flex: 0 0 ${PHOTO_WIDTH}px;
  width: ${PHOTO_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PhotoButton = styled.button`
  appearance: none;
  display: block;
  width: ${PHOTO_WIDTH}px;
  height: 148px;
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border-default);
  background: var(--surface-2);
  cursor: zoom-in;
`;

const Photo = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
`;

const Caption = styled.figcaption`
  font-size: 12px;
  line-height: 1.3;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Status = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

function boundsFor(viewport, track) {
  const view = viewport?.clientWidth || 0;
  const content = track?.scrollWidth || 0;
  return { min: Math.min(0, view - content), max: 0 };
}

const AreaCameraCarousel = ({ areaId }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [offset, setOffset] = useState(0);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const velocityRef = useRef(0);
  const pointerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const pressPhotoIdRef = useRef(null);
  const rafRef = useRef(0);

  const place = (value) => {
    offsetRef.current = value;
    setOffset(value);
  };

  const stopCoast = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    velocityRef.current = 0;
  };

  useEffect(() => {
    if (!areaId) return undefined;
    let cancelled = false;
    setLoading(true);
    place(0);
    areaCamerasService
      .list(areaId, { limit: LATEST })
      .then((list) => {
        if (!cancelled) setPhotos(Array.isArray(list) ? list.slice(0, LATEST) : []);
      })
      .catch(() => {
        if (!cancelled) setPhotos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [areaId]);

  if (loading) {
    return (
      <Status role="status">
        <BeatLoader size={7} color="var(--accent)" />
        Carregando fotos…
      </Status>
    );
  }

  if (photos.length === 0) {
    return <Status>Nenhuma foto de câmera nesta área ainda.</Status>;
  }

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    stopCoast();
    const button = event.target.closest?.("[data-photo-id]");
    pressPhotoIdRef.current = button?.dataset.photoId ?? null;
    pointerRef.current = {
      id: event.pointerId,
      originX: event.clientX,
      lastX: event.clientX,
      lastT: performance.now(),
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    const pointer = pointerRef.current;
    if (!pointer || event.pointerId !== pointer.id) return;
    const now = performance.now();
    const dx = event.clientX - pointer.lastX;
    const dt = Math.max(now - pointer.lastT, 1);
    pointer.lastX = event.clientX;
    pointer.lastT = now;
    if (Math.abs(event.clientX - pointer.originX) > 6) pointer.moved = true;
    velocityRef.current = dx / dt;
    const { min, max } = boundsFor(viewportRef.current, trackRef.current);
    const next = Math.min(max, Math.max(min, offsetRef.current + dx));
    place(next);
  };

  const coast = () => {
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - last, 34);
      last = now;
      velocityRef.current *= Math.exp(-dt / 320);
      const { min, max } = boundsFor(viewportRef.current, trackRef.current);
      let next = offsetRef.current + velocityRef.current * dt;
      if (next > max || next < min) {
        next = Math.min(max, Math.max(min, next));
        velocityRef.current = 0;
      }
      place(next);
      if (Math.abs(velocityRef.current) > 0.02) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const finishDrag = (event) => {
    const pointer = pointerRef.current;
    if (!pointer || event.pointerId !== pointer.id) return;
    if (pointer.moved) suppressClickRef.current = true;
    const photoId = pressPhotoIdRef.current;
    pressPhotoIdRef.current = null;
    if (!pointer.moved && photoId) {
      const photo = photos.find((item) => String(item.id) === String(photoId));
      if (photo) setViewer(photo);
    }
    const idle = performance.now() - pointer.lastT;
    pointerRef.current = null;
    if (idle > 60) velocityRef.current = 0;
    if (Math.abs(velocityRef.current) > 0.05) coast();
  };

  const onPhotoClick = (photo) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setViewer(photo);
  };

  return (
    <Strip
      ref={viewportRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
    >
      <Track ref={trackRef} $x={offset}>
        {photos.map((photo) => (
          <Slide key={photo.id}>
            <PhotoButton
              type="button"
              draggable={false}
              data-photo-id={photo.id}
              aria-label={`Abrir ${photo.original_name || "foto"} em tela cheia`}
              onClick={() => onPhotoClick(photo)}
              onDragStart={(event) => event.preventDefault()}
            >
              <Photo
                src={photo.image_url}
                alt={photo.original_name || "Foto de câmera"}
                draggable={false}
              />
            </PhotoButton>
            <Caption title={photo.original_name}>
              {photo.original_name || "Foto"}
            </Caption>
          </Slide>
        ))}
      </Track>
      <CameraPhotoViewer
        src={viewer?.image_url}
        alt={viewer?.original_name || "Foto de câmera"}
        onClose={() => setViewer(null)}
      />
    </Strip>
  );
};

export default AreaCameraCarousel;
